import { MetadataStore } from '../storage/metadata';
import { getDefaultView, ALLOWED_RESULT_VIEWS } from './matrixMapping';
import { ProjectStore } from '../storage/projects';
import { Project } from '../../types';

export class URLManager {
  private static instance: URLManager;
  private static readonly RESERVED_IDENTIFIERS = ['recent'];
  private listeners: ((identifier: string | null, page: string, view: string | null) => void)[] = [];
  private lastPath: string | null = null;

  // Define allowed views per page
  private static readonly ALLOWED_VIEWS = {
    network: ['editor', 'cities', 'routes', 'help'],
    results: ALLOWED_RESULT_VIEWS
  };

  private constructor() {
    // Listen for URL changes
    window.addEventListener('popstate', () => {
      const currentPath = window.location.pathname;
      if (currentPath !== this.lastPath) {
        console.log('URL changed via browser navigation (back/forward):', {
          from: this.lastPath,
          to: currentPath,
          trigger: 'user',
          source: 'browser_navigation',
          timestamp: new Date().toISOString()
        });
        this.lastPath = currentPath;
        this.notifyListeners();
      }
    });
  }

  static getInstance(): URLManager {
    if (!URLManager.instance) {
      URLManager.instance = new URLManager();
    }
    return URLManager.instance;
  }

  updateURL(identifier: string | null, page: string = 'network', view: string | null = null, trigger: 'user' | 'auto' = 'user', source?: string): void {
    const url = new URL(window.location.href);
    const oldPath = url.pathname;
    
    if (identifier) {
      let path = `/project/${identifier}/${page}`;
      // Only append view if explicitly provided and it's a valid view for the page
      if (view && this.validateView(page, view)) {
        path += `/${view}`;
      }
      url.pathname = path;
    } else {
      url.pathname = '/';
    }

    // Only update if the path actually changed
    if (url.pathname !== oldPath) {
      console.debug('URL update:', {
        from: oldPath,
        to: url.pathname,
        trigger,
        timestamp: new Date().toISOString()
      });
      this.lastPath = url.pathname;
      window.history.pushState({}, '', url.toString());
    }
  }

  getCurrentIdentifier(): string | null {
    const match = window.location.pathname.match(/^\/project\/([^/]+)/);
    if (!match) return null;
    
    const identifier = match[1];
    if (identifier === 'recent') {
      // Handle the special "recent" case by redirecting to the most recent project
      const mostRecent = MetadataStore.getMostRecentProject();
      if (mostRecent) {
        console.log('Redirecting from "recent" to most recent project:', {
          projectId: mostRecent.id,
          projectName: mostRecent.name,
          timestamp: new Date().toISOString()
        });
        // Use replaceState to avoid adding a new history entry
        const url = new URL(window.location.href);
        const currentPage = this.getCurrentPage();
        // Load the full project to get its identifier
        const store = new ProjectStore();
        store.init().then(() => {
          store.getProject(mostRecent.id).then((project: Project | null) => {
            if (project) {
              url.pathname = `/project/${project.identifier}/${currentPage}`;
              window.history.replaceState({}, '', url.toString());
              this.lastPath = url.pathname;
            }
          });
        });
        return mostRecent.id;
      } else {
        console.warn('No recent project found, redirecting to home');
        return null;
      }
    }
    return identifier;
  }

  isReservedIdentifier(identifier: string): boolean {
    return URLManager.RESERVED_IDENTIFIERS.includes(identifier);
  }

  getCurrentPage(): string {
    const match = window.location.pathname.match(/^\/project\/[^/]+\/([^/]+)/);
    return match ? match[1] : 'network';
  }

  getCurrentView(): string | null {
    const match = window.location.pathname.match(/^\/project\/[^/]+\/[^/]+\/([^/]+)/);
    return match ? match[1] : null;
  }

  validateView(page: string, view: string | null): boolean {
    if (!view) return true; // No view is valid (will use default)
    
    // Only network and results pages have views
    if (page !== 'network' && page !== 'results') {
      return false; // Other pages don't support views
    }
    
    const allowedViews = URLManager.ALLOWED_VIEWS[page as keyof typeof URLManager.ALLOWED_VIEWS];
    const isValid = allowedViews ? allowedViews.includes(view) : false;
    
    if (!isValid) {
      console.debug('Invalid view:', {
        page,
        view,
        allowedViews: allowedViews || [],
        reason: !allowedViews ? 'page does not support views' : 'view not allowed for page'
      });
    }
    return isValid;
  }

  getDefaultView(page: string): string | null {
    // Only results page and network page have default views
    if (page === 'results') {
      const allowedViews = URLManager.ALLOWED_VIEWS[page as keyof typeof URLManager.ALLOWED_VIEWS];
      const defaultView = allowedViews ? allowedViews[0] : null;
      return defaultView;
    }
    if (page === 'network') {
      return 'editor';
    }
    return null;
  }

  handleURLChange(callback: (identifier: string | null, page: string, view: string | null) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private notifyListeners(): void {
    const identifier = this.getCurrentIdentifier();
    const page = this.getCurrentPage();
    const view = this.getCurrentView();
    this.listeners.forEach(listener => listener(identifier, page, view));
  }
} 